import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { firstValueFrom } from 'rxjs';
import { DataUnavailableComponent } from '../../Pages/common/data-unavailable/data-unavailable.component';
import { LoaderComponent } from '../../Pages/common/loader/loader.component';
import { ShimmerLoaderComponent } from "../shimmer-loader/shimmer-loader.component";
import { ApiService } from '../../Services/api';
import { AlertService } from '../../Services/notifications/alert.service';
import { AuthService, TokenService } from '../../Services/auth';

@Component({
    selector: 'app-otb-question-detail',
    standalone: true,
    imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatTooltipModule, DataUnavailableComponent, LoaderComponent, ShimmerLoaderComponent],
    templateUrl: './otb-question-detail.component.html',
    styleUrls: ['./otb-question-detail.component.scss']
})
export class OtbQuestionDetailComponent implements OnInit {
    @Input() data: any;
    @Input() userName?: string | null;
    @Input() userId?: string | number | null;
    @Input() questionId?: string | number | null;
    isLoading: boolean = true;

    newReplyText = '';
    newReplyError = '';

    showInlineReply: Record<number, boolean> = {};
    inlineReplyText: Record<number, string> = {};
    inlineReplyError: Record<number, string> = {};

    editingReply: Record<number, boolean> = {};
    editText: Record<number, string> = {};
    editError: Record<number, string> = {};
    editingNested: Record<string, boolean> = {};
    editNestedText: Record<string, string> = {};

    constructor(private apiService: ApiService, private alertService: AlertService, private authService: AuthService, private tokenService: TokenService, private cd: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        console.log('OtbQuestionDetailComponent data:', this.data, 'questionId:', this.questionId);
        console.debug('OtbQuestionDetailComponent current userName:', this.userName);

        if (this.data && this.data.question) {
            this.ensureRepliesArray();
            this.isLoading = false;
        } else {
            this.data = null;
            this.isLoading = true;
        }

        const id = this.questionId || (this.data && this.data.question && this.data.question.id);
        if (id) {
            this.fetchQuestionWithFallback(id.toString()).then(() => {
                this.ensureRepliesArray();
                this.isLoading = false;
            }).catch((err) => {
                console.warn('Failed to load question detail from API', err);
                this.alertService?.show('Unable to load latest question details.', { type: 'error', duration: 3000 });
                this.isLoading = false;
            });
        } else {
            this.data = null;
            this.isLoading = false;
        }

        if (!this.userName && this.data && this.data.userName) {
            this.userName = this.data.userName;
        }
        if (!this.userId && this.data && this.data.userId) {
            this.userId = this.data.userId;
        }

        try {
            if (!this.userId) {
                const authUserId = this.authService?.getCurrentUserId && this.authService.getCurrentUserId();
                if (authUserId) this.userId = authUserId;
                else {
                    const profile = this.tokenService?.getUserProfile && this.tokenService.getUserProfile();
                    if (profile && profile.id) this.userId = profile.id;
                }
            }
            if (!this.userName) {
                const profile = this.tokenService?.getUserProfile && this.tokenService.getUserProfile();
                if (profile && profile.name) this.userName = profile.name;
            }
        } catch (e) {
            console.debug('Failed to read user identity from AuthService/TokenService', e);
        }

        if (this.data && this.data.question && Array.isArray(this.data.question.replies)) {
            this.data.question.replies.forEach((r: any, idx: number) => {
                console.debug(`Reply[${idx}] author raw:`, r.author, 'formatted:', this.formatAuthor(r.author));
            });
        }
    }

    private async fetchQuestionWithFallback(id: string): Promise<void> {
        try {
            const resp = await firstValueFrom(this.apiService.get(`/community/question/${id}`));
            console.log('Posted inline reply, updated replies: 22', resp);

            if (resp && resp.data) {
                this.data = { question: resp.data };
            }
        } catch (err: any) {
            const msg = err?.error || err?.message || err;
            console.debug(`Attempt to GET question detail failed:`, msg);
            if (err?.status === 401 || (err?.error && typeof err.error === 'string' && err.error.toLowerCase().includes('token'))) {
                this.alertService?.show('You must be signed in to view this message.', { type: 'info', duration: 4000 });
                throw new Error('Unauthorized');
            }
            throw err;
        }
        this.isLoading = false;
    }

    private ensureRepliesArray() {
        if (this.data && this.data.question && !Array.isArray(this.data.question.replies)) {
            this.data.question.replies = [];
        }
    }

    trackByIndex = (_: number, __: any) => _;

    submitReply() {
        const text = (this.newReplyText || '').trim();
        if (!text) {
            this.newReplyError = 'Response is required';
            return;
        }
        const qId = this.data && this.data.question ? this.data.question.id : this.questionId;
        if (qId) {
            this.apiService.post(`/community/question/${qId}/reply`, { message: text }).subscribe({
                next: (resp) => {
                    let added: any = resp?.data || { author: this.userName || 'You', message: text, timeAgo: 'Just now', isOfficial: false };
                    if (added) {
                        if (!added.authorId && this.userId !== undefined && this.userId !== null) {
                            added.authorId = this.userId;
                        }
                        if (!added.author) {
                            added.author = this.userName || this.userId || 'You';
                        }
                        if (!added.createdAt) {
                            added.createdAt = new Date().toISOString();
                        }
                        if (!added.content) {
                            if (added.message) added.content = added.message;
                            else if (added.body) added.content = added.body;
                        }
                        if (!added.id) {
                            added.id = String(Date.now()) + '-' + Math.random().toString(36).substr(2, 9);
                        }
                    }
                    this.data.question.replies = this.data.question.replies || [];
                    this.data.question.replies.unshift(added);
                    this.data.question.responses = (this.data.question.responses || 0) + 1;
                    this.newReplyText = '';
                    this.newReplyError = '';
                    this.alertService?.show('Reply posted', { type: 'success', duration: 2500 });
                    console.log('Posted reply, added object:', added);
                    try { this.cd.detectChanges(); } catch (e) { /* ignore */ }
                    try { if (added && added.id) this.scrollToReplyById(String(added.id)); } catch (e) { /* ignore */ }

                },
                error: (err) => {
                    console.error('Failed to post reply', err);
                    this.alertService?.show(err?.error?.message || 'Unable to post reply at this time', { type: 'error', duration: 4000 });
                }
            });
        } else {
            const reply = { id: String(Date.now()), authorId: this.userId || this.userName, author: this.userName || this.userId || 'You', content: text, timeAgo: 'Just now', createdAt: new Date().toISOString(), isOfficial: false };
            this.data.question.replies = this.data.question.replies || [];
            this.data.question.replies.unshift(reply);
            this.data.question.responses = (this.data.question.responses || 0) + 1;
            this.newReplyText = '';
            this.newReplyError = '';
            try { this.cd.detectChanges(); } catch (e) { /* ignore */ }
            try { if (reply && reply.id) this.scrollToReplyById(String(reply.id)); } catch (e) { /* ignore */ }
        }
    }

    cancelTopReply() {
        this.newReplyText = '';
    }

    toggleInlineReply(i: number) {
        this.showInlineReply[i] = !this.showInlineReply[i];
        if (!this.showInlineReply[i]) this.inlineReplyText[i] = '';
    }

    submitInlineReply(i: number) {
        const text = (this.inlineReplyText[i] || '').trim();
        if (!text) {
            this.inlineReplyError[i] = 'Response is required';
            return;
        }
        const qId = this.data && this.data.question ? this.data.question.id : this.questionId;
        const parent = this.data?.question?.replies && this.data.question.replies[i];
        if (qId) {
            this.apiService.post(`/community/question/${qId}/reply`, { message: text }).subscribe({
                next: (resp) => {
                    let added: any = resp?.data || { author: this.userName || 'You', message: text, timeAgo: 'Just now', isOfficial: false };
                    if (added) {
                        if (!added.authorId && this.userId !== undefined && this.userId !== null) {
                            added.authorId = this.userId;
                        }
                        if (!added.author) {
                            added.author = this.userName || this.userId || 'You';
                        }
                        if (!added.createdAt) {
                            added.createdAt = new Date().toISOString();
                        }
                        if (!added.content) {
                            if (added.message) added.content = added.message;
                            else if (added.body) added.content = added.body;
                        }
                        if (!added.id) {
                            added.id = String(Date.now()) + '-' + Math.random().toString(36).substr(2, 9);
                        }
                    }
                    parent.replies = parent.replies || [];
                    parent.replies.push(added);
                    parent.repliesCount = parent.replies.length || (parent.repliesCount || 0) + 1;
                    this.data.question.responses = (this.data.question.responses || 0) + 1;
                    this.inlineReplyText[i] = '';
                    this.inlineReplyError[i] = '';
                    // keep the inline/nested replies visible so the newly added reply is rendered
                    this.showInlineReply[i] = true;
                    this.alertService?.show('Reply posted', { type: 'success', duration: 2500 });
                    console.log('Posted nested reply, added object:', added);
                    try { this.cd.detectChanges(); } catch (e) { /* ignore */ }
                    try { if (added && added.id) this.scrollToReplyById(String(added.id)); } catch (e) { /* ignore */ }
                },
                error: (err) => {
                    console.error('Failed to post inline reply', err);
                    this.alertService?.show(err?.error?.message || 'Unable to post reply at this time', { type: 'error', duration: 4000 });
                }
            });
        } else {
            const reply = { id: String(Date.now()), authorId: this.userId || this.userName, author: this.userName || this.userId || 'You', content: text, timeAgo: 'Just now', createdAt: new Date().toISOString(), isOfficial: false };
            parent.replies = parent.replies || [];
            parent.replies.push(reply);
            parent.repliesCount = parent.replies.length || (parent.repliesCount || 0) + 1;
            this.data.question.responses = (this.data.question.responses || 0) + 1;
            this.inlineReplyText[i] = '';
            this.inlineReplyError[i] = '';
            // keep the inline/nested replies visible so the newly added reply is rendered
            this.showInlineReply[i] = true;
            try { this.cd.detectChanges(); } catch (e) { /* ignore */ }
            try { if (reply && reply.id) this.scrollToReplyById(String(reply.id)); } catch (e) { /* ignore */ }
        }
    }

    cancelInlineReply(i: number) {
        this.inlineReplyText[i] = '';
        this.showInlineReply[i] = false;
    }

    private parseTimeAgoToDate(timeAgo: any): Date | null {
        if (!timeAgo) return null;
        if (timeAgo instanceof Date) return timeAgo;
        const asString = String(timeAgo).trim();
        const maybeDate = new Date(asString);
        if (!isNaN(maybeDate.getTime())) return maybeDate;
        if (/just now/i.test(asString)) return new Date();
        const m = asString.match(/(\d+)\s+minute/);
        if (m) {
            const mins = parseInt(m[1], 10);
            const d = new Date();
            d.setMinutes(d.getMinutes() - mins);
            return d;
        }
        return null;
    }

    canEdit(replyOrTime: any): boolean {
        if (!replyOrTime) return false;

        let timeVal: any = replyOrTime;
        if (typeof replyOrTime === 'object') {
            timeVal = replyOrTime.timeAgo || replyOrTime.createdAt || replyOrTime.timestamp || replyOrTime.time;
        }

        const d = this.parseTimeAgoToDate(timeVal);
        if (!d) return false;
        const diffMs = Date.now() - d.getTime();
        return diffMs >= 0 && diffMs <= 2 * 60 * 1000; // 2 minutes
    }

    isAuthor(reply: any): boolean {
        if (!reply) return false;
        try {
            // Build canonical current id candidates (prefer explicit inputs, then AuthService, then TokenService profile)
            const profile = this.tokenService?.getUserProfile && this.tokenService.getUserProfile();
            const candidates: string[] = [];
            if (this.userId !== undefined && this.userId !== null) candidates.push(String(this.userId));
            if (this.authService?.getCurrentUserId) {
                const aid = this.authService.getCurrentUserId();
                if (aid !== undefined && aid !== null) candidates.push(String(aid));
            }
            if (this.tokenService?.getUserId) {
                const tid = this.tokenService.getUserId();
                if (tid !== undefined && tid !== null) candidates.push(String(tid));
            }
            if (profile && profile.id) candidates.push(String(profile.id));

            // Deduplicate candidates
            const uniqueCandidates = Array.from(new Set(candidates.map(c => c.trim()))).filter(Boolean);
            if (uniqueCandidates.length === 0) return false;

            // Collect possible author id fields from reply (flat and nested)
            const authorIds = new Set<string>();
            const addIf = (v: any) => { if (v !== undefined && v !== null) authorIds.add(String(v).trim()); };

            addIf(reply.authorId);
            addIf(reply.author_id);
            addIf(reply.userId);
            addIf(reply.user_id);
            addIf(reply.createdBy);
            addIf(reply.created_by);
            addIf(reply.id);

            if (reply.author && typeof reply.author === 'object') {
                addIf(reply.author.userId);
                addIf(reply.author.user_id);
                addIf(reply.author.id);
                addIf(reply.author.uid);
            }

            // Compare any candidate against any author id
            for (const cid of uniqueCandidates) {
                if (authorIds.has(cid)) return true;
            }

            return false;
        } catch (e) {
            return false;
        }
    }

    startEdit(i: number, reply: any) {
        this.editingReply[i] = true;
        this.editText[i] = reply.content || reply.message || reply.body || '';
        this.editError[i] = '';
    }

    cancelEdit(i: number) {
        this.editingReply[i] = false;
        this.editText[i] = '';
        this.editError[i] = '';
    }

    submitEdit(i: number, reply: any) {
        const text = (this.editText[i] || '').trim();
        if (!text) {
            this.editError[i] = 'Response is required';
            return;
        }
        this.editError[i] = '';
        const qId = this.data && this.data.question ? this.data.question.id : this.questionId;
        const payload = { content: text };
        if (qId && reply && reply.id) {
            this.apiService.post(`/community/question/${qId}/reply/${reply.id}/edit`, payload).subscribe({
                next: (resp) => {
                    const updated = resp?.data || { ...reply, content: text };
                    // apply updated fields
                    reply.content = updated.content || updated.message || updated.body || text;
                    this.editingReply[i] = false;
                    this.editText[i] = '';
                    this.alertService?.show('Reply updated', { type: 'success', duration: 2500 });
                    try { this.cd.detectChanges(); } catch (e) { /* ignore */ }
                },
                error: (err) => {
                    console.error('Failed to edit reply', err);
                    this.alertService?.show(err?.error?.message || 'Unable to edit reply at this time', { type: 'error', duration: 4000 });
                }
            });
        } else {
            // local-only edit
            reply.content = text;
            this.editingReply[i] = false;
            this.editText[i] = '';
        }
    }

    submitNestedEdit(parent: any, index: number) {
        const key = parent.id + '-' + index;
        const text = (this.editNestedText[key] || '').trim();
        if (!text) return;
        const qId = this.data && this.data.question ? this.data.question.id : this.questionId;
        const nestedReply = parent.replies && parent.replies[index];
        if (!nestedReply) return;
        if (qId && nestedReply && nestedReply.id) {
            this.apiService.post(`/community/question/${qId}/reply/${nestedReply.id}/edit`, { content: text }).subscribe({
                next: (resp) => {
                    const updated = resp?.data || { ...nestedReply, content: text };
                    nestedReply.content = updated.content || updated.message || updated.body || text;
                    this.editingNested[key] = false;
                    this.editNestedText[key] = '';
                    this.alertService?.show('Reply updated', { type: 'success', duration: 2500 });
                    try { this.cd.detectChanges(); } catch (e) { /* ignore */ }
                },
                error: (err) => {
                    console.error('Failed to edit nested reply', err);
                    this.alertService?.show(err?.error?.message || 'Unable to edit reply at this time', { type: 'error', duration: 4000 });
                }
            });
        } else {
            nestedReply.content = text;
            this.editingNested[key] = false;
            this.editNestedText[key] = '';
        }
    }

    formatAuthor(author: any): string {
        if (!author && author !== 0) return '';
        if (typeof author === 'string') return author;
        if (typeof author === 'number') return String(author);
        try {
            if (author.userId) return String(author.userId);
            if (author.user_id) return String(author.user_id);
            if (author.name) return author.name;
            if (author.fullName) return author.fullName;
            if (author.username) return author.username;
            if (author.firstName || author.lastName) return `${author.firstName || ''}${author.firstName && author.lastName ? ' ' : ''}${author.lastName || ''}`.trim();
            if (author.id) return String(author.id);
            return JSON.stringify(author);
        } catch (e) {
            return String(author);
        }
    }

    private scrollToReplyById(id: string) {
        if (!id) return;
        try {
            const selector = `[data-reply-id="${CSS.escape(id)}"]`;
            const start = performance.now();
            const timeout = 2000; // ms

            const tryFind = () => {
                const el = document.querySelector(selector) as HTMLElement | null;
                if (el) {
                    try {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    } catch (e) {
                        el.scrollIntoView();
                    }
                    return true;
                }
                if (performance.now() - start < timeout) {
                    requestAnimationFrame(tryFind);
                    return false;
                }
                return false;
            };

            // kick off after a micro-delay to let change detection settle
            setTimeout(() => { tryFind(); }, 20);
        } catch (e) {
            // ignore
        }
    }
}
